// Device-specific performance curve schemas for active devices
// Fan, blower, and compressor each have their own curve structure
// matching the C++ serializers.

import { z } from 'zod';
import { ValueWithUnitSchema, PressureSettingSchema } from './common';

// ============================================================================
// Fan Curve (C++ fan_curve_serializer.h)
// ============================================================================

const FanTableTypeSchema = z.enum(['static', 'total']);
const FanInputCurveTypeSchema = z.enum(['power', 'efficiency']);

const FanCurveTableEntrySchema = z.object({
  dP: ValueWithUnitSchema,
  flow: ValueWithUnitSchema,
  power: ValueWithUnitSchema.optional(),
  efficiency: ValueWithUnitSchema.optional(),
});

/** Value range used for speed_range and stall_range on fan curves. */
const ValueRangeSchema = z.object({
  min: ValueWithUnitSchema.optional(),
  max: ValueWithUnitSchema.optional(),
});

export const FanCurveSchema = z.object({
  curve_name: z.string(),
  manufacturer: z.string().optional(),
  model: z.string().optional(),
  test_speed: ValueWithUnitSchema.optional(),
  reference_density: ValueWithUnitSchema.optional(),
  speed_range: ValueRangeSchema.optional(),
  stall_range: ValueRangeSchema.optional(),
  table: z.array(FanCurveTableEntrySchema),
  table_type: FanTableTypeSchema,
  input_curve_type: FanInputCurveTypeSchema,
});

export type FanCurve = z.infer<typeof FanCurveSchema>;

// ============================================================================
// Compressor Curve (C++ compressor_curve_serializer.h)
// ============================================================================

const CompressorCurveTableEntrySchema = z.object({
  flow: ValueWithUnitSchema,
  pressure_ratio: ValueWithUnitSchema,
  efficiency: ValueWithUnitSchema,
});

export const CompressorCurveSchema = z.object({
  curve_name: z.string(),
  manufacturer: z.string().optional(),
  model: z.string().optional(),
  test_speed: ValueWithUnitSchema.optional(),
  flow_unit: z.string().optional(),
  reference_molecular_mass: ValueWithUnitSchema.optional(),
  reference_specific_heat_ratio: ValueWithUnitSchema.optional(),
  reference_pressure: ValueWithUnitSchema.optional(),
  reference_temperature: ValueWithUnitSchema.optional(),
  table: z.array(CompressorCurveTableEntrySchema),
});

export type CompressorCurve = z.infer<typeof CompressorCurveSchema>;

// ============================================================================
// Blower Curve (C++ blower_curve_serializer.h)
// ============================================================================

const BlowerPressureDropRowSchema = z.object({
  speed: ValueWithUnitSchema,
  flow: ValueWithUnitSchema,
  power: ValueWithUnitSchema.optional(),
  delta_temperature: ValueWithUnitSchema.optional(),
});

const BlowerPressureDropDatasetSchema = z.object({
  label: z.string(),
  pressure_drop_line: PressureSettingSchema,
  pressure_drop_table: z.array(BlowerPressureDropRowSchema),
});

export const BlowerCurveSchema = z.object({
  curve_name: z.string(),
  manufacturer: z.string().optional(),
  model: z.string().optional(),
  max_flow: ValueWithUnitSchema.optional(),
  max_pressure_drop: ValueWithUnitSchema.optional(),
  max_speed: ValueWithUnitSchema.optional(),
  max_vacuum: ValueWithUnitSchema.optional(),
  reference_density: ValueWithUnitSchema.optional(),
  tables: z.array(BlowerPressureDropDatasetSchema),
});

export type BlowerCurve = z.infer<typeof BlowerCurveSchema>;

// ============================================================================
// Pump Curve (real stored pump_curve, post-import)
// ============================================================================
//
// Step-0 enumeration (no guessing) — sources:
//   - shared/src/hydraulic/import/desktop-json-transformer.ts
//       transformPumpCurve() / tryAdoptOperatingCurve() / applyG1Selection()
//   - packages/calc-engine/src/devices/PumpDevice.test.ts:946-968
//
// Observed real stored shape (the import path produces, and what is stored on
// the vertex's pump_curve):
//   top-level:
//     curve_type      string  — observed values: 'manual_curve', 'catalog_curve'
//                               (NOT an invented enum; real free-form field)
//     raw_curves      array (>=1)
//     selected_curve  object  — { curve_type:'diameter'|'speed'|'label',
//                                 diameter_value?/speed_hz?/label? }  (optional)
//     operating_curve object  — PIPE-FLO C++ trim+speed-corrected curve points.
//                               PERSISTS on the stored model: tryAdoptOperatingCurve
//                               (desktop-json-transformer.ts:973) reads it but does
//                               NOT delete the field, so it's a reliable signal that
//                               raw_curves[0] is the operating curve.
//   raw_curves[i]:
//     curve_type    'diameter'|'speed'|'label'   (optional)
//     diameter_value number  — in MILLIMETERS per the live-probe contract in
//                              service-curve-to-pump-curve.ts:13 (optional)
//     speed_rpm      number  — injected by transformer from operation.value
//                              (calc requires it on raw_curves[0]; optional here)
//     label          string                       (optional)
//     is_operating_curve boolean  — true when raw_curves[0] was synthesized
//                                   from operating_curve by tryAdoptOperatingCurve.
//                                   Calc engine skips affinity scaling when set
//                                   (the points are already trim+speed-corrected).
//     points         array (>=1)
//
//   Top-level pump-data fields (PIPE-FLO "Enter Pump Data" dialog parity):
//     test_speed     ValueWithUnit  — curve reference speed (rpm or Hz). Required
//                                     by calc engine; pre-calc analyzer gates the
//                                     calc with an error if not resolvable.
//     test_diameter  ValueWithUnit  — curve reference impeller diameter.
//     min_speed, max_speed       ValueWithUnit  — mechanical bounds (advisory).
//     min_diameter, max_diameter ValueWithUnit  — impeller trim bounds (advisory).
//     manufacturer, size         string         — catalog metadata.
//     flow_unit, head_unit, diameter_unit string — user-preferred display units;
//                                                  storage is always SI.
//   points[j]:
//     flow, head        number  OR  { value:number, unit:string }
//     efficiency?, NPSHr?, power?  number  OR  { value, unit }
//     (legacy 'npsh' lowercase tolerated via passthrough)
//
//   IMPORTANT (Task 6 widening): the real import path (desktop-json-transformer
//   transformPumpCurve → applyG1Selection) returns the selected raw_curve with
//   its points UN-flattened — flow/head are still {value,unit}-shaped Desktop
//   `dynamic_unit_value`s at that point (the G1' selection branch returns early
//   before normalizeVertexToSI flattens them, and the round-trip-test fixtures
//   that mirror Liquid Transfer carry exactly this shape). Bare-number points
//   (manual_curve, synthesized affinity-law curves) also occur. The schema must
//   therefore accept BOTH. Widened from number-only to number|ValueWithUnit so
//   replacing z.unknown() does not reject real transformer output.
//
// The schema is a PERMISSIVE SUPERSET (.passthrough() + optional fields) so
// replacing z.unknown() cannot break existing imported models.

// number OR Desktop {value,unit} dynamic_unit_value — both are real point shapes.
const NumberOrValueWithUnit = z.union([z.number(), ValueWithUnitSchema]);

// Real stored pump_curve (post-import). See Step 0 enumeration. Permissive superset.
export const PumpCurvePointSchema = z.object({
  flow: NumberOrValueWithUnit,
  head: NumberOrValueWithUnit,
  efficiency: NumberOrValueWithUnit.optional(),
  NPSHr: NumberOrValueWithUnit.optional(),     // existing casing — back-compat
  power: NumberOrValueWithUnit.optional(),     // NEW additive field (spec §3.1)
}).passthrough();
export type PumpCurvePoint = z.infer<typeof PumpCurvePointSchema>;  // REQUIRED: index.ts:129 + centrifugal-pump.ts re-export depend on this

const PumpRawCurveSchema = z.object({
  curve_type: z.enum(['diameter', 'speed', 'label']).optional(),
  diameter_value: z.number().optional(),         // MILLIMETERS per live-probe contract
  speed_rpm: z.number().positive().optional(),   // calc requires on raw_curves[0]; optional at schema level
  label: z.string().optional(),
  // Signals that raw_curves[0] was synthesized from operating_curve by
  // tryAdoptOperatingCurve and is already trim+speed-corrected by PIPE-FLO C++.
  // Calc engine skips affinity scaling when this is true.
  is_operating_curve: z.boolean().optional(),
  points: z.array(PumpCurvePointSchema).min(1),
}).passthrough();

export const PumpCurveSchema = z.object({
  curve_type: z.string(),                         // real top-level field (NOT an invented enum)
  raw_curves: z.array(PumpRawCurveSchema).min(1),
  // Zod v4 (project is on zod 4.3.6) requires the 2-arg z.record signature;
  // the codebase-wide pattern (see document-header.ts:27) is
  // z.record(z.string(), z.unknown()). Same intent as the plan's permissive
  // arbitrary-keyed object — single-arg z.record is the removed zod v3 API.
  selected_curve: z.record(z.string(), z.unknown()).optional(),
  // C++-converter pump configuration block. Only selected_stages is typed:
  // the user's selected stage count for multi-stage catalog pumps (lineshaft
  // turbine / ESP / VSS). raw_curves/operating_curve carry PER-STAGE head;
  // the calc engine multiplies head by this count. Absent → single-stage.
  pump_config: z
    .object({
      selected_stages: z.number().int().min(1).optional(),
    })
    .passthrough()
    .optional(),
  pump_flo_selection: z.object({                  // optional, additive provenance
    manufacturer: z.string(),
    model: z.string(),
    catalog_id: z.string(),
    pump_ref: z.string(),
    catalog_snapshot: z.string().optional(),
    selected_at: z.string(),
  }).optional(),

  // PIPE-FLO "Enter Pump Data" dialog fields (parity).
  //
  // Test conditions are required by the calc engine for affinity scaling.
  // Optional at the Zod layer to keep already-stored manual_curve models
  // loadable; the UI requires both before save, and the pre-calc analyzer
  // (PumpCurveCompletenessAnalyzer) gates the calc with an error annotation
  // if no test speed is resolvable for a specified_speed-mode pump.
  test_speed: ValueWithUnitSchema.optional(),     // { value, unit: 'rpm' | 'Hz' }
  test_diameter: ValueWithUnitSchema.optional(),  // { value, unit: 'in' | 'mm' | 'm' }

  // Mechanical bounds — advisory. PumpCurveBoundsAnalyzer emits warning-severity
  // annotations when operating conditions fall outside these bounds.
  min_speed:    ValueWithUnitSchema.optional(),
  max_speed:    ValueWithUnitSchema.optional(),
  min_diameter: ValueWithUnitSchema.optional(),
  max_diameter: ValueWithUnitSchema.optional(),

  // Catalog metadata (PIPE-FLO "Additional Data" block).
  manufacturer: z.string().optional(),
  size:         z.string().optional(),

  // User's preferred display units. Curve points are stored in SI; these
  // strings round-trip the dialog's entry-unit selections so reopening
  // shows the same labels and units the user typed in. Strings only —
  // no conversion is driven from these fields. See unit-converter.ts for
  // canonical spellings (e.g. 'm3/s' with ASCII '3', not 'm³/s').
  flow_unit:     z.string().optional(),
  head_unit:     z.string().optional(),
  diameter_unit: z.string().optional(),
}).passthrough();
export type PumpCurve = z.infer<typeof PumpCurveSchema>;
